import { Suspense } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { LocationGenerics, useNavigateWithSlug } from "routes";
import invariant from "tiny-invariant";

import {
  ListOrchestrationWorkflowsDocument,
  OrchestrationWorkflowDocument,
  useDeleteWorkflowMutation,
  useEditWorkflowMutation,
  useOrchestrationWorkflowSuspenseQuery,
} from "@/apollo/types";
import { Button } from "@/components/elements/Button";
import confirm from "@/components/elements/Confirm";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { Heading } from "@/components/elements/Typography";
import { AdvancedSyncScheduler } from "@/components/modules/WeldSyncScheduler";
import { FieldErrorMessage } from "@/components/primitives/FieldErrorMessage";
import FieldLabel from "@/components/primitives/InputLabel";
import { Input } from "@/components/primitives/input";
import { TextArea } from "@/components/primitives/input/TextArea";
import { getCronInfo } from "@/hooks/useCron";
import { useToast } from "@/providers/ToastProvider";
import { useMatch } from "@tanstack/react-location";

import { OrchestrationTabsLayout } from "./OrchestrationTabsLayout";

const defaultFallbackCron = "0 0 * * *";

const WorkflowSettings = () => {
  const match = useMatch<LocationGenerics>();
  const navigate = useNavigateWithSlug();
  return (
    <OrchestrationTabsLayout current="configuration">
      <div className="flex flex-col gap-6">
        <div>
          <Heading className="mb-2">Settings</Heading>
          <div className="max-w-screen-sm">
            <Suspense
              fallback={
                <div>
                  <LoadingSpinner />
                </div>
              }
            >
              <EditOrchestrationForm workflowId={match.params.workflowId} />
            </Suspense>
          </div>{" "}
        </div>
        <hr />
        <div>
          <Heading className="mb-2">Delete this orchestration</Heading>
          <p className="mb-4 text-sm text-muted-foreground">
            Deleting this orchestration will remove it permanently from the
            system. All jobs currently scheduled in this orchestration will
            revert to running individually on a default schedule.
          </p>
          <DeleteOrchestrationButtonContainer
            workflowId={match.params.workflowId}
            onDeleted={() => {
              navigate({ to: "/orchestration" });
            }}
          />
        </div>
      </div>
    </OrchestrationTabsLayout>
  );
};

export default WorkflowSettings;

function EditOrchestrationForm(props: { workflowId: string }) {
  const toast = useToast();

  const { data } = useOrchestrationWorkflowSuspenseQuery({
    variables: {
      workflowId: props.workflowId,
    },
  });
  const workflow = data?.orchestrationWorkflow;
  invariant(workflow, "Workflow not found");

  const formMethods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: workflow.name,
      description: workflow.description || "",
      cronExpression: workflow.cronExpression,
    },
  });

  const [updateWorkflow, updateWorkflowMutation] = useEditWorkflowMutation({
    onCompleted: () => {
      toast(
        "Orchestration updated successfully",
        "The Orchestration schedule has been updated.",
        "success",
      );
    },
    onError: (error) => {
      toast(
        "Failed to update Orchestration",
        error.message || "An error occurred while updating the orchestration.",
        "error",
      );
    },
    refetchQueries: [
      {
        query: OrchestrationWorkflowDocument,
        variables: { workflowId: workflow.id },
      },
    ],
    awaitRefetchQueries: true,
  });

  const onSubmit = (values: {
    name: string;
    description?: string;
    cronExpression: string;
  }) => {
    return updateWorkflow({
      variables: {
        workflowId: workflow.id,
        name: values.name,
        description: values.description,
        cronExpression: values.cronExpression,
      },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form
        id="edit-orchestration-form"
        onSubmit={formMethods.handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-4">
          <div>
            <FieldLabel htmlFor="orchestration-name">Name</FieldLabel>
            <Input
              {...formMethods.register("name", {
                required: "Orchestration name is required.",
              })}
              id="orchestration-name"
              placeholder="Orchestration name"
              size="md"
            />
            <FieldErrorMessage name="name" />
          </div>
          <div>
            <FieldLabel htmlFor="orchestration-description">
              Description
            </FieldLabel>
            <TextArea
              {...formMethods.register("description")}
              id="orchestration-description"
              placeholder="A brief description of the orchestration"
            />
          </div>
          <div>
            <FieldLabel htmlFor="orchestration-schedule">Schedule</FieldLabel>
            <Controller
              name="cronExpression"
              control={formMethods.control}
              render={({ field }) => (
                <AdvancedSyncScheduler
                  cron={field.value}
                  onChange={field.onChange}
                />
              )}
              rules={{
                validate: (value) => {
                  if (!value) {
                    return "Cron expression is required.";
                  }
                  const { isValid } = getCronInfo(value);
                  if (!isValid) {
                    return "Invalid cron expression.";
                  }
                  return true;
                },
              }}
            />
            <FieldErrorMessage name="cronExpression" />
          </div>
          <div className="flex justify-start">
            <Button
              type="submit"
              colorScheme="primary"
              variant="solid"
              size="sm"
              isLoading={updateWorkflowMutation.loading}
              loadingText="Saving..."
            >
              Save changes
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

function DeleteOrchestrationButtonContainer(props: {
  workflowId: string;
  onDeleted: () => void;
}) {
  const toast = useToast();

  const [deleteWorkflow, deleteWorkflowMutation] = useDeleteWorkflowMutation({
    variables: {
      workflowId: props.workflowId,
      cronSchedule: defaultFallbackCron,
    },

    onCompleted: () => {
      toast(
        "Orchestration deleted",
        "Successfully removed orchestration",
        "success",
      );
      props.onDeleted();
    },
    onError: (error) => {
      toast(
        "Error deleting orchestration",
        error.message ??
          "Something went wrong trying to delete the orchestration.",
        "error",
      );
    },
    refetchQueries: [{ query: ListOrchestrationWorkflowsDocument }],
    awaitRefetchQueries: true,
    update: (cache) => {
      cache.evict({ id: `OrchestrationWorkflow:${props.workflowId}` });
    },
  });

  return (
    <Button
      variant="outline"
      colorScheme="danger"
      size="sm"
      onClick={async () => {
        if (
          await confirm({
            title: "Are you sure?",
            message:
              "Are you sure you want to delete this orchestration? This action cannot be undone.",
            type: "danger",
            confirmButtonText: "Delete",
          })
        ) {
          await deleteWorkflow();
        }
      }}
      isLoading={deleteWorkflowMutation.loading}
      loadingText="Deleting..."
    >
      Delete orchestration
    </Button>
  );
}
